import { Checkbox } from "@chakra-ui/react";
import { FiX } from "react-icons/fi";
import { SelectWithLabel } from "newui/components/ui/InputWithLabel";
import { ToggleButtonGroup } from "newui/components/ui/ToggleButtonGroup";
import { TemplateQuickAssign } from "../TemplateQuickAssign";
import type {
	Role,
	RoleTemplate,
	UserAssignment,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";

export type AssignMode = "role" | "template";

interface Step1UserRolePickerProps {
	users: UserAssignment[];
	selectedUserIds: number[];
	onToggleUser: (userId: number) => void;
	onToggleAll: () => void;
	mode: AssignMode;
	onModeChange: (mode: AssignMode) => void;
	roles: Role[];
	selectedRoleId: number | null;
	onSelectRole: (roleId: number | null) => void;
	templates: RoleTemplate[];
	selectedTemplateId: number | null;
	onSelectTemplate: (id: number) => void;
}

const initials = (name: string): string =>
	name
		.split(" ")
		.map((part) => part[0])
		.join("")
		.slice(0, 2)
		.toUpperCase();

// Step 1: multi-select user checkboxes (row-selection pattern) + role OR template choice.
export const Step1UserRolePicker = ({
	users,
	selectedUserIds,
	onToggleUser,
	onToggleAll,
	mode,
	onModeChange,
	roles,
	selectedRoleId,
	onSelectRole,
	templates,
	selectedTemplateId,
	onSelectTemplate,
}: Step1UserRolePickerProps) => {
	const allSelected = users.length > 0 && selectedUserIds.length === users.length;
	const someSelected = selectedUserIds.length > 0 && !allSelected;
	const selectedUsers = users.filter((user) => selectedUserIds.includes(user.id));

	const roleOptions = roles.map((role) => ({ label: role.name, value: role.id }));
	const selectedRoleOption =
		roleOptions.find((option) => option.value === selectedRoleId) ?? null;

	return (
		<div data-testid="wizard-step1">
			<p className="mb-2 text-[11.5px] font-bold uppercase tracking-wide text-gray-600">
				Step 1 — Pick user(s)
			</p>

			<div className="mb-2 flex items-center gap-2 border-b border-gray-200 pb-2">
				<Checkbox
					isChecked={allSelected}
					isIndeterminate={someSelected}
					onChange={onToggleAll}
					data-testid="user-select-all"
				>
					<span className="text-sm text-gray-600">
						{selectedUserIds.length} of {users.length} selected
					</span>
				</Checkbox>
			</div>

			<div className="max-h-56 overflow-auto rounded-md border border-gray-200" data-testid="user-pick-list">
				{users.map((user) => {
					const checked = selectedUserIds.includes(user.id);
					return (
						<div
							key={user.id}
							className="flex items-center gap-2.5 border-b border-gray-100 px-3 py-2 last:border-b-0 hover:bg-blue-50"
						>
							<Checkbox
								isChecked={checked}
								onChange={() => onToggleUser(user.id)}
								aria-label={`Select ${user.name}`}
								data-testid={`user-pick-${user.id}`}
							/>
							<span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-[10.5px] font-semibold text-blue-700">
								{initials(user.name)}
							</span>
							<span className="text-sm font-medium">{user.name}</span>
							<span className="text-xs text-gray-500">{user.email}</span>
						</div>
					);
				})}
			</div>

			{selectedUsers.length > 0 && (
				<div className="mt-3 flex flex-wrap gap-2" data-testid="selected-user-chips">
					{selectedUsers.map((user) => (
						<span
							key={user.id}
							className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2 py-1 text-xs text-blue-700"
						>
							{user.name}
							<button
								type="button"
								onClick={() => onToggleUser(user.id)}
								aria-label={`Remove ${user.name}`}
								data-testid={`selected-user-remove-${user.id}`}
								className="opacity-70 hover:opacity-100"
							>
								<FiX size={12} />
							</button>
						</span>
					))}
				</div>
			)}

			<div className="mt-5">
				<p className="mb-2 text-[11.5px] font-bold uppercase tracking-wide text-gray-600">
					Step 2 — Choose access
				</p>
				<ToggleButtonGroup
					options={[
						{ label: "Quick-assign template", value: "template" },
						{ label: "Single role", value: "role" },
					]}
					value={mode}
					onChange={(value) => onModeChange(value as AssignMode)}
				/>
			</div>

			{mode === "template" ? (
				<div className="mt-4">
					<TemplateQuickAssign
						templates={templates}
						selectedId={selectedTemplateId}
						onSelect={onSelectTemplate}
					/>
				</div>
			) : (
				<div className="mt-4 max-w-sm">
					<SelectWithLabel
						label="Role"
						required
						testId="role-select"
						options={roleOptions}
						value={selectedRoleOption}
						onChange={(option) =>
							onSelectRole(
								option && !Array.isArray(option) ? (option.value as number) : null
							)
						}
						placeholder="Select a role…"
					/>
				</div>
			)}
		</div>
	);
};
